/**
 * THE CODEC: BLOCKS ⇄ THE RESTRICTED MARKDOWN DIALECT. The serialiser and the parser the corpus has
 * named in five places and defined in none.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ WHY IT EXISTS, AND WHO THE THREE CALLERS ARE
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `04-schema.md` [`WC-CR-14` ruled **(a)**]: what goes over the wire to the translation
 * engine "is **markdown**, re-parsed on intake into fresh paragraph and mark rows - the legacy
 * engine has always been given markdown and shipped clients render it, so a plain-text contract
 * would strip bold, italics and links from all thirteen translations of every article on day one."
 * `03-worksheet.md` names this file: "`shared/content-model` serialises `body` + its paragraph
 * rows + its mark rows into the restricted markdown dialect, sends that, and requires markdown
 * back." `research/19-block-content-anchors-research.md` adds the third caller and says it is
 * "not optional": "the translation engine has always been given markdown (`blocks[].m`), the shipped
 * clients have always rendered markdown, and the legacy corpus to be imported *is* markdown.
 * Markdown crosses boundaries; it is never at rest, and no offset is ever measured over it."
 *
 * ★ NO OFFSET IS EVER MEASURED OVER WHAT THIS FILE PRODUCES. That sentence is the whole reason the
 * anchors live next door and not here: `anchors.ts` indexes `body`, this file indexes nothing.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ THE DIALECT, AND WHERE EACH CHARACTER COMES FROM
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `WC-CR-10` was ruled **(a)**: "pin the dialect to what the legacy converter produced with default
 * options, and prove it with the A15 fidelity fixtures" - the point being that "nothing a reader
 * sees changes on launch day". The audit states what those defaults produced, and this file takes
 * its four settled characters from that sentence rather than from any library's documentation:
 *
 *   `docs/audit/domains/content-authoring-editing.md`, verbatim:
 *     "`TurndownService` runs with default options (ATX headings, `*` bullets, `_` emphasis)"
 *   and `:457`: "Markdown produced by `TurndownService` with **default options** (no `headingStyle`,
 *   no `bulletListMarker`, no custom rules)."
 *
 * So: ATX headings (`#`..`####`), `*` bullets, `_` for emphasis, `**` for strong, and `[text](href)`
 * for a link (turndown's default `linkStyle` is inlined, and the legacy corpus carries that form).
 * `WD-51` fixes the rest of the shape: the dialect is "a *closed* list, enforced as a validation
 * error at the API, with no tables, no footnotes, no images inside a paragraph and no raw HTML"
 * (`03-worksheet.md`).
 *
 * ★★ THREE OF THE SIX MARKS HAVE NO TURNDOWN DEFAULT AT ALL, AND THE CHOICE IS RECORDED RATHER THAN
 * DRESSED IN A CITATION. `inline_mark_kind` seeds `underline`, `strike` and `code`
 * (`04-schema.md`, `WC-CR-09` ruled (c), "nothing is taken away from today's author"),
 * and the legacy toolbar emits underline and strikethrough (`content-authoring-editing.md`), so
 * the dialect must carry all three or the corpus cannot be imported - which is the condition
 * `WC-CR-09` puts on every one of its options. The three characters chosen:
 *   `code`       -> `` ` ``     the one universal form, and CommonMark's.
 *   `strike`     -> `~~`        GFM's, which every renderer this platform deploys to already reads.
 *   `underline`  -> `++`        ★ AN UNRULED CHOICE. HTML `<u>` is the obvious form and `WD-51`
 *                               forbids it in terms ("no raw HTML"), `~~` is taken, and `__` would
 *                               collide with `_`. `++` is inert prose in CommonMark, so a renderer
 *                               that does not know the dialect shows the author's own characters
 *                               rather than swallowing their word.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ THE ESCAPING RULES, WHICH ARE WHAT MAKE `parse(serialise(x)) == x` TRUE
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `research/19:730` is the test this file is written to pass: "**Markdown round trip.**
 * `parse(serialise(x)) == x` for every generated block ... Must include: a link inside a list item,
 * nested emphasis, an empty paragraph, and a literal `*` in prose."
 *
 *   1. A BACKSLASH ESCAPES THE NEXT CODE POINT, ANYWHERE, and the parser un-escapes it. That is
 *      turndown's own device and CommonMark's.
 *   2. IN PROSE, EVERY DELIMITER CHARACTER IS ESCAPED WHETHER OR NOT IT WOULD BE READ AS ONE -
 *      `\ * _ ` ~ + [ ]`. Escaping only where it would change meaning is what a human-facing
 *      converter does; a parser that has to be EXACT cannot afford the ambiguity, and the cost is a
 *      backslash the engine hands back unchanged.
 *   3. AT THE START OF A PARAGRAPH, `#`, `>` and `-` are escaped too, and a leading run of digits
 *      followed by `.` has the `.` escaped. None of the three is a delimiter this file emits; they
 *      are escaped for FIDELITY, because a shipped client reads the line as CommonMark and would
 *      otherwise render an author's "- see below" as a bullet.
 *   4. INSIDE A CODE SPAN ONLY `` ` `` AND `\` ARE ESCAPED. Escaping `*` there would put a visible
 *      backslash inside monospace text on every client, since a code span's content is literal.
 *
 * ★ AND TWO SHAPES ARE REFUSED RATHER THAN MANGLED, both by `refuseInexpressibleMarks`:
 *   - a non-code mark that begins or ends strictly inside a code span. A code span's content is
 *     literal in every renderer, so `` `a**b**c` `` shows the asterisks; emitting it would be a lie
 *     about what the reader will see, and dropping the mark would be silent data loss. A mark that
 *     CONTAINS a code span is fine and common - `**an `id` in bold**` - and is emitted.
 *   - two links that overlap. CommonMark: "Links may not contain other links, at any level of
 *     nesting." ★ THIS ONE WAS FOUND BY THE PROPERTY TEST AND NOT BY READING, which is the whole
 *     argument for `12-build-plan.md`'s "property tests and not one example": the generator
 *     produced a link inside a link at seed 1, the encoder wrote `[[a](x)](y)`, and this file's own
 *     parser read it as one link and four literal characters.
 * `ContentCodecError` names the block and both marks in each case.
 *
 * C11: no clock is read here and no instant is taken.
 */
import type { BlockKind, InlineMarkKind, ParagraphStyle } from './blocks';
import {
  DEFAULT_PARAGRAPH_STYLE,
  HEADING_PARAGRAPH_STYLES,
  MAX_LIST_LEVEL,
  headingDepth,
  isListStyle,
} from './blocks';
import type { MarkRow, ParagraphRow } from './anchors';
import { PARAGRAPH_SEPARATOR, codePointsOf, paragraphRangesFor, paragraphRowsFor } from './anchors';

/** One block as this file reads and writes it: its text, and the two row sets beside it. */
export interface CodecBlock {
  readonly blockId: string;
  readonly kind: BlockKind;
  /** `body` - the one authoritative string. `null` on a divider. */
  readonly body: string | null;
  readonly quoteAttribution: string | null;
  readonly imageAlt: string | null;
  readonly paragraphs: readonly ParagraphRow[];
  readonly marks: readonly MarkRow[];
}

/**
 * One block on the wire: the same block with its `body` serialised and its rows folded into it.
 *
 * ★ ONLY `body` BECOMES MARKDOWN. `quoteAttribution` and `imageAlt` are carried through untouched,
 * and that is `WC-CR-15`'s table rather than an omission (`03-worksheet.md`): a quote
 * sends "`body`, `quote_attribution`" and an image "`body` (the caption), `image_alt`" as fields of
 * their own, and only the text block's body is listed "as markdown, carrying its paragraph styles
 * and marks". Neither column can carry a mark row in this model, so escaping them would hand the
 * translation engine a backslash to translate and buy nothing.
 */
export interface EncodedBlock {
  readonly blockId: string;
  readonly kind: BlockKind;
  readonly body: string | null;
  readonly quoteAttribution: string | null;
  readonly imageAlt: string | null;
}

/** A document this file refuses to serialise, naming the block and the reason. */
export class ContentCodecError extends Error {
  constructor(
    readonly blockId: string,
    detail: string,
  ) {
    super(`block ${blockId} cannot be serialised into the dialect: ${detail}`);
    this.name = 'ContentCodecError';
  }
}

const ESCAPE = '\\';
const ALWAYS_ESCAPED: ReadonlySet<string> = new Set([ESCAPE, '*', '_', '`', '~', '+', '[', ']']);
const PARAGRAPH_START_ESCAPED: ReadonlySet<string> = new Set(['#', '>', '-']);
const IN_CODE_ESCAPED: ReadonlySet<string> = new Set([ESCAPE, '`']);
const DIGITS: ReadonlySet<string> = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);

/** The paired delimiter of every mark but the link, which is bracketed and handled on its own. */
const MARK_DELIMITER: Readonly<Record<Exclude<InlineMarkKind, 'link'>, string>> = {
  strong: '**',
  em: '_',
  underline: '++',
  strike: '~~',
  code: '`',
};

/**
 * The order marks are OPENED in when several begin at the same code point.
 *
 * ★ `code` IS LAST AND THAT IS NOT COSMETIC: a code span binds tighter than emphasis in CommonMark,
 * so `**a `b` c**` is strong text containing a code span while `` `a **b** c` `` is literal
 * asterisks in monospace. Opening `code` innermost is what keeps a shipped client's rendering equal
 * to the author's intent. It is also the reason the refusal above exists for the one arrangement
 * this order cannot save.
 */
const MARK_OPEN_ORDER: readonly InlineMarkKind[] = [
  'strong',
  'em',
  'underline',
  'strike',
  'link',
  'code',
];

/** The blank line between two paragraphs of one block's markdown. */
const MARKDOWN_PARAGRAPH_BREAK = '\n\n';

/** Turndown's default bullet, per `content-authoring-editing.md`. */
const BULLET_MARKER = '* ';
/** The ordered-list marker. Always `1.`: CommonMark numbers the list itself. */
const ORDERED_MARKER = '1. ';
/** One level of list nesting, as two spaces. */
const LIST_INDENT = '  ';

// ------------------------------------------------------------------------------------------------
// Canonical form
// ------------------------------------------------------------------------------------------------

function markOrderOf(kind: InlineMarkKind): number {
  const index = MARK_OPEN_ORDER.indexOf(kind);
  return index < 0 ? MARK_OPEN_ORDER.length : index;
}

/**
 * ★★ THE CANONICAL MARK SET, and the three things it does - each of which the round trip needs.
 *
 *   1. CLAMPED AND DROPPED. A mark past the end of the text, or with `endCp <= startCp`, is not a
 *      mark (`ck_awmark_range`, and "a zero-width mark is not a mark", `authors-api.yaml`).
 *   2. SPLIT AT PARAGRAPH BOUNDARIES. A mark that spans two paragraphs has no single markdown form -
 *      the paragraphs are serialised separately - and the separator itself belongs to no paragraph.
 *      A selection dragged across a paragraph break is therefore TWO runs, which is what every
 *      renderer already shows and what the parser reads back.
 *   3. MERGED WHEN ADJACENT OR OVERLAPPING AND OF THE SAME KIND AND HREF. Two touching runs of one
 *      kind are indistinguishable from one in the markdown, in the database's natural key
 *      `(block_key, start_cp, end_cp, kind)` and on every screen. Merging is what makes the round
 *      trip an identity rather than a near-miss: the encoder splits an overlap into nested pieces
 *      and the parser hands the pieces back, and only this rule puts them together again.
 *
 * The result is sorted in the read order `research/19:178` names - "all marks of a block, in start
 * order" - with the kind breaking a tie so the order is total.
 */
export function canonicaliseMarks(text: string, marks: readonly MarkRow[]): readonly MarkRow[] {
  const length = codePointsOf(text).length;
  const clamped = marks
    .map((mark) => ({
      ...mark,
      startCp: Math.max(0, Math.min(mark.startCp, length)),
      endCp: Math.max(0, Math.min(mark.endCp, length)),
    }))
    .filter((mark) => mark.endCp > mark.startCp);

  const pieces: MarkRow[] = [];
  for (const range of paragraphRangesFor(text)) {
    for (const mark of clamped) {
      const startCp = Math.max(mark.startCp, range.startCp);
      const endCp = Math.min(mark.endCp, range.endCp);
      if (endCp > startCp) {
        pieces.push({ ...mark, startCp, endCp });
      }
    }
  }

  pieces.sort(
    (left, right) =>
      markOrderOf(left.kind) - markOrderOf(right.kind) ||
      (left.linkHref ?? '').localeCompare(right.linkHref ?? '') ||
      left.startCp - right.startCp ||
      left.endCp - right.endCp,
  );

  const merged: MarkRow[] = [];
  for (const piece of pieces) {
    const last = merged[merged.length - 1];
    if (
      last !== undefined &&
      last.kind === piece.kind &&
      last.linkHref === piece.linkHref &&
      piece.startCp <= last.endCp
    ) {
      merged[merged.length - 1] = { ...last, endCp: Math.max(last.endCp, piece.endCp) };
      continue;
    }
    merged.push(piece);
  }

  return merged.sort(
    (left, right) =>
      left.startCp - right.startCp ||
      left.endCp - right.endCp ||
      markOrderOf(left.kind) - markOrderOf(right.kind),
  );
}

/**
 * One block in the form the codec round-trips exactly: derived paragraph ranges carrying the
 * author's styles, and canonical marks.
 *
 * IT REPAIRS RATHER THAN REFUSES, AND THE REQUEST BOUNDARY DOES THE OPPOSITE. Two different jobs:
 * `article-editor.schema.ts` refuses a client whose rows disagree with the text, because an author
 * is standing there and a silent repair is data loss; this function is used where there IS no
 * client - the translation intake, the migration import, a property test's generated document - and
 * a canonical form is what those need.
 */
export function canonicaliseBlock(block: CodecBlock): CodecBlock {
  if (block.body === null) {
    return { ...block, paragraphs: [], marks: [] };
  }
  return {
    ...block,
    paragraphs: block.kind === 'text' ? paragraphRowsFor(block.body, block.paragraphs) : [],
    marks:
      block.kind === 'text' || block.kind === 'quote'
        ? canonicaliseMarks(block.body, block.marks)
        : [],
  };
}

// ------------------------------------------------------------------------------------------------
// Inline: text + marks -> markdown
// ------------------------------------------------------------------------------------------------

function escapeRun(run: readonly string[], atParagraphStart: boolean, inCode: boolean): string {
  const escaped: string[] = [];
  const set = inCode ? IN_CODE_ESCAPED : ALWAYS_ESCAPED;
  for (const [index, character] of run.entries()) {
    const startOfParagraph = atParagraphStart && index === 0;
    if (
      set.has(character) ||
      (!inCode && startOfParagraph && PARAGRAPH_START_ESCAPED.has(character))
    ) {
      escaped.push(ESCAPE, character);
      continue;
    }
    escaped.push(character);
  }
  if (!inCode && atParagraphStart) {
    return escapeLeadingOrderedMarker(escaped, run);
  }
  return escaped.join('');
}

/** `12.` at the start of a paragraph is an ordered list to a renderer; the `.` is escaped. */
function escapeLeadingOrderedMarker(escaped: readonly string[], run: readonly string[]): string {
  let digits = 0;
  while (digits < run.length && DIGITS.has(run[digits] ?? '')) {
    digits += 1;
  }
  if (digits === 0 || run[digits] !== '.') {
    return escaped.join('');
  }
  // Nothing before the digits was escaped (a digit is in no escape set), so the arrays align here.
  return [...escaped.slice(0, digits), ESCAPE, ...escaped.slice(digits)].join('');
}

function openToken(mark: MarkRow): string {
  return mark.kind === 'link' ? '[' : MARK_DELIMITER[mark.kind];
}

function closeToken(mark: MarkRow): string {
  if (mark.kind !== 'link') {
    return MARK_DELIMITER[mark.kind];
  }
  const href = codePointsOf(mark.linkHref ?? '')
    .map((character) =>
      character === ')' || character === '(' || character === ESCAPE
        ? ESCAPE + character
        : character,
    )
    .join('');
  return `](${href})`;
}

/**
 * Refuse the two arrangements the dialect cannot express - see the header's last paragraph.
 *
 * ★ (1) A non-code mark must be DISJOINT from every code span or must CONTAIN it. Anything else
 * would put a delimiter inside literal monospace text.
 *
 * ★★ (2) TWO LINKS MAY NOT OVERLAP, AND THAT ONE WAS FOUND BY THE PROPERTY TEST RATHER THAN
 * REASONED OUT. Seed 1 generated a link inside a link; the encoder emitted `[[a](x)](y)`, which
 * CommonMark forbids in terms - "Links may not contain other links, at any level of nesting" - and
 * which this file's own parser read as ONE link plus four characters of literal text, so
 * `decode(encode(d))` was not `d`. A partial overlap is the same shape once the encoder has nested
 * the pieces. Neither is repairable by choosing a different character: there IS no markdown for a
 * link inside a link, so the honest answer is the refusal `WD-51` describes - "a *closed* list,
 * enforced as a validation error at the API".
 */
function refuseInexpressibleMarks(blockId: string, marks: readonly MarkRow[]): void {
  const codeSpans = marks.filter((mark) => mark.kind === 'code');
  for (const span of codeSpans) {
    for (const mark of marks) {
      if (mark.kind === 'code') {
        continue;
      }
      const disjoint = mark.endCp <= span.startCp || mark.startCp >= span.endCp;
      const contains = mark.startCp <= span.startCp && mark.endCp >= span.endCp;
      if (!disjoint && !contains) {
        throw new ContentCodecError(
          blockId,
          `a '${mark.kind}' mark spans [${String(mark.startCp)},${String(mark.endCp)}) and the ` +
            `code span [${String(span.startCp)},${String(span.endCp)}) crosses its edge. A code ` +
            "span's content is literal in every renderer, so the mark could only be emitted as " +
            'visible characters or dropped, and both are wrong',
        );
      }
    }
  }

  const links = marks.filter((mark) => mark.kind === 'link');
  for (const [index, link] of links.entries()) {
    for (const other of links.slice(index + 1)) {
      if (other.startCp < link.endCp && link.startCp < other.endCp) {
        throw new ContentCodecError(
          blockId,
          `two link marks overlap - [${String(link.startCp)},${String(link.endCp)}) to ` +
            `${String(link.linkHref)} and [${String(other.startCp)},${String(other.endCp)}) to ` +
            `${String(other.linkHref)}. CommonMark forbids a link inside a link at any level of ` +
            'nesting, so there is no form of this to emit and no reader that could follow both',
        );
      }
    }
  }
}

/** One paragraph's text and its marks (rebased to the paragraph) as one line of markdown. */
function encodeInline(blockId: string, text: string, marks: readonly MarkRow[]): string {
  refuseInexpressibleMarks(blockId, marks);
  const cps = codePointsOf(text);
  const boundaries = new Set<number>([0, cps.length]);
  for (const mark of marks) {
    boundaries.add(mark.startCp);
    boundaries.add(mark.endCp);
  }
  const points = [...boundaries].sort((left, right) => left - right);

  const open: MarkRow[] = [];
  const out: string[] = [];
  for (let index = 0; index + 1 < points.length; index += 1) {
    const from = points[index] ?? 0;
    const to = points[index + 1] ?? 0;
    const active = marks.filter((mark) => mark.startCp <= from && mark.endCp >= to);

    let keep = 0;
    while (keep < open.length && active.includes(open[keep] as MarkRow)) {
      keep += 1;
    }
    for (let depth = open.length - 1; depth >= keep; depth -= 1) {
      out.push(closeToken(open[depth] as MarkRow));
    }
    open.length = keep;

    for (const mark of active
      .filter((candidate) => !open.includes(candidate))
      .sort(
        (left, right) =>
          markOrderOf(left.kind) - markOrderOf(right.kind) || right.endCp - left.endCp,
      )) {
      out.push(openToken(mark));
      open.push(mark);
    }

    const inCode = open.some((mark) => mark.kind === 'code');
    out.push(escapeRun(cps.slice(from, to), from === 0, inCode));
  }
  for (let depth = open.length - 1; depth >= 0; depth -= 1) {
    out.push(closeToken(open[depth] as MarkRow));
  }
  return out.join('');
}

// ------------------------------------------------------------------------------------------------
// Inline: markdown -> text + marks
// ------------------------------------------------------------------------------------------------

interface DecodedInline {
  readonly text: string;
  readonly marks: readonly MarkRow[];
}

/**
 * ★ EVERY MARK BUT THE LINK IS A TOGGLE, AND THAT IS DELIBERATE RATHER THAN LAZY.
 *
 * The encoder emits properly nested delimiters, so a stack would do - for text THIS file wrote. What
 * comes back from a translation engine is not that text: it is a human or a model's re-rendering of
 * it, and `03-worksheet.md` says the mark set "may legitimately differ from the source". A
 * toggle reads `**a_b**c_` - improperly nested, and the natural thing for a translator to produce -
 * as the two overlapping runs it visibly is, where a stack would drop one of them on the floor.
 * Overlapping marks are legal here: "Marks **overlay** the body (they may overlap each other and
 * leave gaps); paragraphs **partition** it" (`research/19:178`).
 *
 * A mark still open at the end of the text is closed there rather than discarded, for the same
 * reason: an engine that returns `**bold` has formatted something, and the something is the rest.
 */
function decodeInline(source: string): DecodedInline {
  const cps = codePointsOf(source);
  const out: string[] = [];
  const marks: MarkRow[] = [];
  const open = new Map<InlineMarkKind, number>();
  let inCode = false;
  let index = 0;

  const toggle = (kind: InlineMarkKind): void => {
    const start = open.get(kind);
    if (start === undefined) {
      open.set(kind, out.length);
      return;
    }
    open.delete(kind);
    if (out.length > start) {
      marks.push({ startCp: start, endCp: out.length, kind, linkHref: null });
    }
  };

  while (index < cps.length) {
    const character = cps[index] ?? '';
    const next = cps[index + 1];
    if (character === ESCAPE && index + 1 < cps.length) {
      out.push(cps[index + 1] ?? '');
      index += 2;
      continue;
    }
    if (inCode) {
      if (character === '`') {
        inCode = false;
        toggle('code');
        index += 1;
        continue;
      }
      out.push(character);
      index += 1;
      continue;
    }
    if (character === '`') {
      inCode = true;
      toggle('code');
      index += 1;
      continue;
    }
    if (character === '*' && next === '*') {
      toggle('strong');
      index += 2;
      continue;
    }
    if (character === '+' && next === '+') {
      toggle('underline');
      index += 2;
      continue;
    }
    if (character === '~' && next === '~') {
      toggle('strike');
      index += 2;
      continue;
    }
    if (character === '_') {
      toggle('em');
      index += 1;
      continue;
    }
    if (character === '[') {
      const link = readLink(cps, index);
      if (link !== null) {
        const inner = decodeInline(link.text);
        const start = out.length;
        out.push(...codePointsOf(inner.text));
        for (const mark of inner.marks) {
          marks.push({ ...mark, startCp: mark.startCp + start, endCp: mark.endCp + start });
        }
        if (out.length > start) {
          marks.push({ startCp: start, endCp: out.length, kind: 'link', linkHref: link.href });
        }
        index = link.next;
        continue;
      }
    }
    out.push(character);
    index += 1;
  }

  for (const [kind, start] of open) {
    if (out.length > start) {
      marks.push({ startCp: start, endCp: out.length, kind, linkHref: null });
    }
  }

  const text = out.join('');
  return { text, marks: canonicaliseMarks(text, marks) };
}

interface ReadLink {
  readonly text: string;
  readonly href: string;
  readonly next: number;
}

/** `[text](href)` from `open`, or `null` when the shape is not there and the `[` is a character. */
function readLink(cps: readonly string[], open: number): ReadLink | null {
  let index = open + 1;
  const text: string[] = [];
  while (index < cps.length) {
    const character = cps[index] ?? '';
    if (character === ESCAPE && index + 1 < cps.length) {
      text.push(character, cps[index + 1] ?? '');
      index += 2;
      continue;
    }
    if (character === ']') {
      break;
    }
    text.push(character);
    index += 1;
  }
  if (cps[index] !== ']' || cps[index + 1] !== '(') {
    return null;
  }
  index += 2;
  const href: string[] = [];
  while (index < cps.length) {
    const character = cps[index] ?? '';
    if (character === ESCAPE && index + 1 < cps.length) {
      href.push(cps[index + 1] ?? '');
      index += 2;
      continue;
    }
    if (character === ')') {
      return { text: text.join(''), href: href.join(''), next: index + 1 };
    }
    href.push(character);
    index += 1;
  }
  return null;
}

// ------------------------------------------------------------------------------------------------
// Paragraph prefixes
// ------------------------------------------------------------------------------------------------

function paragraphPrefix(style: ParagraphStyle, listLevel: number): string {
  const depth = headingDepth(style);
  if (depth !== null) {
    return `${'#'.repeat(depth)} `;
  }
  if (isListStyle(style)) {
    return LIST_INDENT.repeat(listLevel) + (style === 'ul' ? BULLET_MARKER : ORDERED_MARKER);
  }
  return '';
}

interface ParsedPrefix {
  readonly style: ParagraphStyle;
  readonly listLevel: number;
  readonly rest: string;
}

/** Read a paragraph's style off its line. Anything unrecognised is a `p` and keeps every character. */
function parseParagraphPrefix(line: string): ParsedPrefix {
  const heading = /^(#{1,4}) /.exec(line);
  if (heading !== null) {
    const hashes = heading[1] ?? '';
    const style = HEADING_PARAGRAPH_STYLES[hashes.length - 1];
    if (style !== undefined) {
      return { style, listLevel: 0, rest: line.slice(hashes.length + 1) };
    }
  }
  const list = /^( *)(\* |\d+\. )/.exec(line);
  if (list !== null) {
    const indent = (list[1] ?? '').length;
    const marker = list[2] ?? '';
    const listLevel = indent / LIST_INDENT.length;
    if (Number.isInteger(listLevel) && listLevel <= MAX_LIST_LEVEL) {
      return {
        style: marker.startsWith('*') ? 'ul' : 'ol',
        listLevel,
        rest: line.slice(indent + marker.length),
      };
    }
  }
  return { style: DEFAULT_PARAGRAPH_STYLE, listLevel: 0, rest: line };
}

// ------------------------------------------------------------------------------------------------
// Blocks
// ------------------------------------------------------------------------------------------------

/**
 * ★★ ONE BLOCK'S TEXT, SERIALISED. The unit is the BLOCK and not the document, which is
 * `research/19:730`'s own unit ("for every generated block") and both consumers': the translation
 * engine is sent "a list of `(block_id, field, text)`" (`research/19:301`), and the compatibility
 * edge fills `t` and `m` per block (`research/19:700-706`). So no block separator has to be
 * invented, and no parser ever has to find one.
 *
 * ★ PARAGRAPHS ARE JOINED BY A BLANK LINE, AND ONE CASE DEVIATES FROM THE LEGACY BYTES ON PURPOSE.
 * The legacy rule is `content-authoring-editing.md`: a paragraph is "appended to `m` with
 * `"\n\n"`", and an "empty paragraph (`''`, `<br>`, or blank) appends a bare `"\n"`". The first half
 * is taken verbatim. The second cannot be: a bare `\n` is indistinguishable from part of a
 * separator, so an empty paragraph would not survive `parse(serialise(x)) == x` - and an empty
 * paragraph is one of the four fixtures `research/19-block-content-anchors-research.md` and
 * `research/19-block-content-anchors-research.md` require to round-trip, because
 * "an author who presses Enter twice keeps the blank line" (`authors-api.yaml`). An empty
 * paragraph is therefore an empty line between two separators, which renders identically (a
 * CommonMark renderer collapses the blank lines either way) and parses exactly.
 */
export function encodeBlock(block: CodecBlock): EncodedBlock {
  const canonical = canonicaliseBlock(block);
  const head = {
    blockId: canonical.blockId,
    kind: canonical.kind,
    quoteAttribution: canonical.quoteAttribution,
    imageAlt: canonical.imageAlt,
  };
  if (canonical.body === null) {
    return { ...head, body: null };
  }
  if (canonical.kind === 'quote') {
    return { ...head, body: encodeInline(canonical.blockId, canonical.body, canonical.marks) };
  }
  if (canonical.kind !== 'text') {
    // An image block's `body` is its caption: plain text with no rows of either sort. See the
    // `EncodedBlock` header - `WC-CR-15` sends it as a field of its own.
    return { ...head, body: canonical.body };
  }
  const lines = canonical.paragraphs.map((paragraph) => {
    const text = codePointsOf(canonical.body ?? '')
      .slice(paragraph.startCp, paragraph.endCp)
      .join('');
    const marks = canonical.marks
      .filter((mark) => mark.startCp >= paragraph.startCp && mark.endCp <= paragraph.endCp)
      .map((mark) => ({
        ...mark,
        startCp: mark.startCp - paragraph.startCp,
        endCp: mark.endCp - paragraph.startCp,
      }));
    return (
      paragraphPrefix(paragraph.style, paragraph.listLevel) +
      encodeInline(canonical.blockId, text, marks)
    );
  });
  return { ...head, body: lines.join(MARKDOWN_PARAGRAPH_BREAK) };
}

/** The inverse. See `encodeBlock` for every rule it undoes. */
export function decodeBlock(encoded: EncodedBlock): CodecBlock {
  const head = {
    blockId: encoded.blockId,
    kind: encoded.kind,
    quoteAttribution: encoded.quoteAttribution,
    imageAlt: encoded.imageAlt,
  };
  if (encoded.body === null) {
    return { ...head, body: null, paragraphs: [], marks: [] };
  }
  if (encoded.kind === 'quote') {
    const inline = decodeInline(encoded.body);
    return { ...head, body: inline.text, paragraphs: [], marks: inline.marks };
  }
  if (encoded.kind !== 'text') {
    return { ...head, body: encoded.body, paragraphs: [], marks: [] };
  }
  const texts: string[] = [];
  const styles: { style: ParagraphStyle; listLevel: number }[] = [];
  const marks: MarkRow[] = [];
  let offset = 0;
  for (const line of encoded.body.split(MARKDOWN_PARAGRAPH_BREAK)) {
    const prefix = parseParagraphPrefix(line);
    const inline = decodeInline(prefix.rest);
    texts.push(inline.text);
    styles.push({ style: prefix.style, listLevel: prefix.listLevel });
    for (const mark of inline.marks) {
      marks.push({ ...mark, startCp: mark.startCp + offset, endCp: mark.endCp + offset });
    }
    // The separator that will sit between this paragraph and the next belongs to neither of them.
    offset += codePointsOf(inline.text).length + PARAGRAPH_SEPARATOR.length;
  }
  const body = texts.join(PARAGRAPH_SEPARATOR);
  return {
    ...head,
    body,
    paragraphs: paragraphRowsFor(body, styles),
    marks: canonicaliseMarks(body, marks),
  };
}

/** Every block of a document, each serialised on its own. */
export function encodeDocument(blocks: readonly CodecBlock[]): readonly EncodedBlock[] {
  return blocks.map((block) => encodeBlock(block));
}

/** The inverse, block for block and in order. */
export function decodeDocument(encoded: readonly EncodedBlock[]): readonly CodecBlock[] {
  return encoded.map((block) => decodeBlock(block));
}
